import { Box } from '@mui/material'
import type { ReactNode } from 'react'
import { useTokens } from '../theme'

/**
 * The brand's orbit motif, used as the unread marker: a tilted ring behind the avatar with a gold planet on it.
 * Flat strokes only. Children (the avatar) render on top of the ring, so only its tips and the planet show.
 */
export function OrbitAvatar({ unread, size = 40, children }: { unread: boolean; size?: number; children: ReactNode }) {
  const t = useTokens()
  const pad = 8
  const box = size + pad * 2
  const c = box / 2
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      {unread && (
        <Box component="svg" aria-hidden viewBox={`0 0 ${box} ${box}`} sx={{ position: 'absolute', top: -pad, left: -pad, width: box, height: box, pointerEvents: 'none' }}>
          <g transform={`rotate(-28 ${c} ${c})`}>
            <ellipse cx={c} cy={c} rx={size / 2 + 6} ry={size / 2 - 8} fill="none" stroke={t.accent} strokeOpacity={0.55} strokeWidth={1.5} />
          </g>
        </Box>
      )}
      <Box sx={{ position: 'relative' }}>{children}</Box>
      {unread && (
        <Box component="svg" aria-hidden viewBox={`0 0 ${box} ${box}`} sx={{ position: 'absolute', top: -pad, left: -pad, width: box, height: box, pointerEvents: 'none' }}>
          <g transform={`rotate(-28 ${c} ${c})`}>
            <circle cx={c + (size / 2 + 6) * Math.cos(0.5)} cy={c + (size / 2 - 8) * Math.sin(0.5)} r={4} fill={t.gold} stroke={t.surface} strokeWidth={1.5} />
          </g>
        </Box>
      )}
    </Box>
  )
}

/** Empty-state mark: two orbit rings with a planet, the given icon at the centre. */
export function OrbitMark({ children }: { children: ReactNode }) {
  const t = useTokens()
  return (
    <Box sx={{ position: 'relative', width: 128, height: 128, mx: 'auto', color: 'text.secondary', display: 'grid', placeItems: 'center', '& > svg.icon, & > .icon svg': { fontSize: 40 } }}>
      <Box component="svg" aria-hidden viewBox="0 0 128 128" sx={{ position: 'absolute', inset: 0 }}>
        <circle cx="64" cy="64" r="60" fill="none" stroke="currentColor" strokeOpacity={0.22} strokeWidth={1.5} />
        <circle cx="64" cy="64" r="42" fill="none" stroke="currentColor" strokeOpacity={0.22} strokeWidth={1.5} />
        <circle cx="106" cy="34" r="6" fill={t.gold} />
      </Box>
      <Box className="icon" sx={{ position: 'relative', display: 'grid', placeItems: 'center', opacity: 0.75 }}>{children}</Box>
    </Box>
  )
}
